import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  MenuItem,
  IconButton,
  Paper,
  Card,
  CardContent,
  Avatar,
  InputAdornment,
  Alert,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Assignment as TicketIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Build as JobIcon,
  Devices as DeviceIcon,
  BrandingWatermark as BrandIcon,
  Category as ModelIcon,
  QrCode2 as SerialIcon,
  Notes as NotesIcon,
  Event as DateIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import api from "../../../services/api";
import { getIndustrySummary, getBrands, getModels, Brand } from "../../../services/catalog";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { useClients } from "../../../hooks/useClients";
import AddCustomerModal from "../../pos/components/AddCustomerModal";

// ---- Local Types ------------------------------------------------------------
type Client = { _id: string; fullName: string; [k: string]: any };
type CategoryItem = { name: string; [k: string]: any };

type SelectField = {
  label: string;
  field: keyof FormState;
  options: any[]; // Client[] | CategoryItem[]
  valueKey?: string; // defaults to "name"
  labelKey?: string; // defaults to "name"
  required?: boolean;
};

type FormState = {
  clientId: string;
  jobType: string;
  deviceType: string;
  brand: string;
  model: string;
  serialOrImei: string;
  notes: string;
  estimatedFinishDate: string; // yyyy-mm-dd
  assignedTo?: string;
};

// ---- Component --------------------------------------------------------------
export default function CreateTicketPage() {
  useProtectedRoute();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const heroGradient = useMemo(
    () =>
      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 55%, #0f172a 100%)`,
    [theme]
  );
  const surfaceBorder = `1px solid ${theme.palette.mode === "light" ? "#e5e7eb" : theme.palette.divider}`;

  const rawClients = (useClients() as Client[]) || [];
  const [clientOptions, setClientOptions] = useState<Client[]>([]);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);

  const [, setIndustryKey] = useState<string>("electronics");
  const [jobTypes, setJobTypes] = useState<CategoryItem[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<CategoryItem[]>([]);
  const [brands, setBrands] = useState<CategoryItem[]>([]);
  const [catalogBrands, setCatalogBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<CategoryItem[]>([]);
  const [loadingCats, setLoadingCats] = useState<boolean>(true);
  const [ticketFields, setTicketFields] = useState<Array<{ id: string; label: string; type: 'text' | 'number' | 'boolean' }>>([]);

  const [newCategory, setNewCategory] = useState<{ type: string; name: string }>({ type: "", name: "" });
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [technicians, setTechnicians] = useState<Array<{ _id: string; name: string }>>([]);
  const [extras, setExtras] = useState<Record<string, any>>({});
  useEffect(() => {
    setClientOptions(rawClients);
  }, [rawClients]);

  // read preselected client from location.state or ?clientId=
  const presetClientId = useMemo(() => {
    const stateId = (location.state as any)?.clientId && String((location.state as any).clientId);
    const queryId = new URLSearchParams(location.search).get("clientId") || "";
    return stateId || queryId || "";
  }, [location.state, location.search]);

  const [form, setForm] = useState<FormState>({
    clientId: presetClientId,
    jobType: "",
    deviceType: "",
    brand: "",
    model: "",
    serialOrImei: "",
    notes: "",
    estimatedFinishDate: "",
  });

  // keep clientId in sync if route changes
  useEffect(() => {
    if (presetClientId && form.clientId !== presetClientId) {
      setForm((f) => ({ ...f, clientId: presetClientId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetClientId]);

  // Load industry-aware categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (!token) return;
      try {
        setLoadingCats(true);
        setServerError(null);
        const headers = { Authorization: `Bearer ${token}` };

        // 1) Get tenant industry
        const me = await api.get("/shops/me", { headers });
        const ind = me?.data?.industryKey || "electronics";
        setIndustryKey(ind);

        // 2) Fetch defaults from catalog
        const summary = await getIndustrySummary(ind, token);
        const defaultJobs = (summary?.jobTypes || []).map((name) => ({ name }));
        const defaultDevices = (summary?.deviceTypes || []).map((name) => ({ name }));
        setTicketFields(summary?.ticketFields || []);

        // 3) Fetch tenant custom categories
        const [jobRes, deviceRes, brandRes] = await Promise.all([
          api.get("/categories/jobType", { headers }),
          api.get("/categories/deviceType", { headers }),
          api.get("/categories/brand", { headers }),
        ]);

        // merge defaults + custom (unique by name)
        const mergeByName = (a: { name: string }[], b: { name: string }[]) => {
          const seen = new Set<string>();
          const out: { name: string }[] = [];
          [...a, ...b].forEach((it) => {
            const n = (it?.name || "").trim();
            if (!n || seen.has(n.toLowerCase())) return;
            seen.add(n.toLowerCase());
            out.push({ name: it.name });
          });
          return out;
        };

        setJobTypes(mergeByName(defaultJobs, jobRes.data || []));
        setDeviceTypes(mergeByName(defaultDevices, deviceRes.data || []));

        // brands: catalog + custom
        const catBrands = await getBrands(ind, token);
        setCatalogBrands(catBrands);
        const mergedBrands = mergeByName(
          catBrands.map((b) => ({ name: b.name })),
          brandRes.data || []
        );
        setBrands(mergedBrands);
        setModels([]); // models depend on selected brand
      } catch (e: any) {
        setServerError("Failed to load categories.");
      } finally {
        setLoadingCats(false);
      }
    };
    fetchCategories();
  }, [token]);

  // When brand changes, load models from catalog and merge tenant custom models (no strict mapping yet)
  useEffect(() => {
    const loadModels = async () => {
      if (!token) return;
      const bname = form.brand?.trim();
      if (!bname) { setModels([]); return; }
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const brand = catalogBrands.find((b) => b.name.toLowerCase() === bname.toLowerCase());
        const catModels = brand ? await getModels(brand.slug, token) : [];
        // fetch tenant custom models tied to this brand, else fallback to generic models list
        const custom = brand ? await api.get(`/categories/brand/${brand.slug}/models`, { headers }) : await api.get("/categories/model", { headers });
        const mergeByName = (a: { name: string }[], b: { name: string }[]) => {
          const seen = new Set<string>();
          const out: { name: string }[] = [];
          [...a, ...b].forEach((it) => {
            const n = (it?.name || "").trim();
            if (!n || seen.has(n.toLowerCase())) return;
            seen.add(n.toLowerCase());
            out.push({ name: it.name });
          });
          return out;
        };
        const merged = mergeByName(catModels.map((m) => ({ name: m.name })), custom.data || []);
        setModels(merged);
      } catch {
        // ignore silently for UX; leave existing list
      }
    };
    loadModels();
  }, [form.brand, token, catalogBrands]);

  // Load technicians list for assignment
  useEffect(() => {
    const fetchTechs = async () => {
      if (!token) return;
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await api.get('/users/technicians', { headers });
        setTechnicians(res.data || []);
      } catch {
        // ignore silently
      }
    };
    fetchTechs();
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    if (!form.clientId || !/^[a-fA-F0-9]{24}$/.test(form.clientId)) return "Please select a valid client.";
    if (!form.jobType) return "Please select a job type.";
    if (!form.deviceType) return "Please select a device type.";
    return null;
  };
  const handleCustomerCreated = (client: Client) => {
    setClientOptions((prev) => {
      const filtered = prev.filter((c) => c._id !== client._id);
      return [client, ...filtered];
    });
    setForm((f) => ({ ...f, clientId: client._id }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setServerError(err);
      return;
    }
    try {
      setSubmitting(true);
      setServerError(null);
      const payload = { ...form, extras } as any;
      const res = await api.post("/tickets", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate(`/tickets/${res.data._id}`);
    } catch (err: any) {
      setServerError(err?.response?.data?.message || "Failed to create ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const addCategory = async () => {
    if (!newCategory.type || !newCategory.name) return;
    try {
      setSubmitting(true);
      const headers = { Authorization: `Bearer ${token}` };
      const body: any = { type: newCategory.type, name: newCategory.name };
      if (newCategory.type === 'model' && form.brand) {
        const brand = catalogBrands.find((b) => b.name.toLowerCase() === String(form.brand).toLowerCase());
        if (brand) body.parentKey = brand.slug;
      }
      await api.post("/categories", body, { headers });
      const res = await api.get(`/categories/${newCategory.type}`, { headers });

      if (newCategory.type === "jobType") setJobTypes(res.data || []);
      if (newCategory.type === "deviceType") setDeviceTypes(res.data || []);
      if (newCategory.type === "brand") setBrands(res.data || []);
      if (newCategory.type === "model") {
        // If we added a model under current brand, refetch brand-scoped models
        if (form.brand) {
          const b = catalogBrands.find((cb) => cb.name.toLowerCase() === String(form.brand).toLowerCase());
          if (b) {
            const scoped = await api.get(`/categories/brand/${b.slug}/models`, { headers });
            setModels(scoped.data || []);
          } else {
            setModels(res.data || []);
          }
        } else {
          setModels(res.data || []);
        }
      }

      setNewCategory({ type: "", name: "" });
    } catch {
      setServerError("Failed to add category.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectFields: SelectField[] = [
    { label: "Client", field: "clientId", options: clientOptions, valueKey: "_id", labelKey: "fullName", required: true },
    { label: "Job Type", field: "jobType", options: jobTypes, valueKey: "name", labelKey: "name", required: true },
    { label: "Device Type", field: "deviceType", options: deviceTypes, valueKey: "name", labelKey: "name", required: true },
    { label: "Brand", field: "brand", options: brands, valueKey: "name", labelKey: "name" },
    { label: "Model", field: "model", options: models, valueKey: "name", labelKey: "name" },
  ];

  // extras state in form (dynamic industry fields)
  // Note: declared once near the top; remove duplicate declaration below if present.

  // adornment icon map (for subtle visual guidance)
  const adornmentIcon: Record<string, JSX.Element | null> = {
    clientId: <PersonIcon fontSize="small" />,
    jobType: <JobIcon fontSize="small" />,
    deviceType: <DeviceIcon fontSize="small" />,
    brand: <BrandIcon fontSize="small" />,
    model: <ModelIcon fontSize="small" />,
    serialOrImei: <SerialIcon fontSize="small" />,
    notes: <NotesIcon fontSize="small" />,
    estimatedFinishDate: <DateIcon fontSize="small" />,
  };

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      sx={{ p: { xs: 2, md: 3 }, pb: { xs: 1, md: 2 } }}
    >
      {/* HERO / HEADER */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: surfaceBorder,
          background: heroGradient,
          color: "white",
        }}
      >
        <CardContent sx={{ py: { xs: 2.25, md: 2.75 } }}>
          <Grid container alignItems="center" spacing={2} justifyContent="space-between">
            <Grid item xs={12} md="auto">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    width: 44,
                    height: 44,
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <TicketIcon />
                </Avatar>
                <Box>
                  <Typography variant={isSmDown ? "h5" : "h4"} sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                    Create Ticket
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Log a new repair job and capture device details.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: { xs: "stretch", md: "flex-end" },
                  mt: { xs: 1, md: 0 },
                }}
              >
                <Button
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/tickets")}
                  sx={{ color: "white", fontWeight: 800 }}
                >
                  Back to Tickets
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {serverError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {serverError}
        </Alert>
      )}

      {/* CONTENT */}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: surfaceBorder }}>
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Grid container spacing={2.5}>
            {loadingCats ? (
              <>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Grid item xs={12} sm={6} key={`sk-${i}`}>
                    <Skeleton variant="rounded" height={40} />
                  </Grid>
                ))}
              </>
            ) : (
              <>
              {selectFields.map(({ label, field, options, valueKey = "name", labelKey = "name", required }) => (
                <Grid item xs={12} sm={6} key={String(field)}>
                  <TextField
                    fullWidth
                    select
                    required={!!required}
                    label={label}
                    name={field}
                    value={form[field]}
                    onChange={handleChange}
                    size="small"
                    InputProps={{
                      startAdornment: adornmentIcon[field] ? (
                        <InputAdornment position="start">{adornmentIcon[field]}</InputAdornment>
                      ) : undefined,
                    }}
                  >
                    <MenuItem value="">{`Select ${label}`}</MenuItem>
                    {options.map((option: any) => (
                      <MenuItem key={String(option[valueKey])} value={String(option[valueKey])}>
                        {option[labelKey]}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Box sx={{ mt: 1, display: "flex" }}>
                    {field === "clientId" ? (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<AddCircleOutlineIcon fontSize="small" color="success" />}
                        onClick={() => setCustomerModalOpen(true)}
                        sx={{ fontWeight: 700, color: theme.palette.success.main, px: 0 }}
                      >
                        Add Customer
                      </Button>
                    ) : (
                      <IconButton
                        onClick={() => setNewCategory({ type: field as string, name: "" })}
                        aria-label={`Add ${field}`}
                        size="small"
                        sx={{ color: theme.palette.success.main }}
                      >
                        <AddCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
              ))}

              {/* Dynamic industry-specific fields */}
              {ticketFields.map((fld) => (
                <Grid item xs={12} sm={6} key={fld.id}>
                  {fld.type === 'boolean' ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={fld.label}
                      value={String(extras[fld.id] ?? '')}
                      onChange={(e) => setExtras((x) => ({ ...x, [fld.id]: e.target.value === 'true' }))}
                    >
                      <MenuItem value="">Not specified</MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </TextField>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      type={fld.type === 'number' ? 'number' : 'text'}
                      label={fld.label}
                      value={extras[fld.id] ?? ''}
                      onChange={(e) => setExtras((x) => ({ ...x, [fld.id]: fld.type === 'number' ? Number(e.target.value) : e.target.value }))}
                    />
                  )}
                </Grid>
              ))}
              </>
            )}

            {newCategory.type && (
              <Grid item xs={12} sm={8} md={6}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    label={`New ${newCategory.type}`}
                    fullWidth
                    size="small"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  />
                  <Button variant="contained" onClick={addCategory} disabled={submitting}>
                    Add
                  </Button>
                </Box>
              </Grid>
            )}

            {/* Assign Technician (optional) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Assign Technician (optional)"
                name="assignedTo"
                value={form.assignedTo || ''}
                onChange={handleChange}
                size="small"
              >
                <MenuItem value="">Unassigned</MenuItem>
                {technicians.map((t) => (
                  <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Serial / IMEI / Reg #"
                name="serialOrImei"
                value={form.serialOrImei}
                onChange={handleChange}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">{adornmentIcon.serialOrImei}</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Diagnostic Notes"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                multiline
                rows={3}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">{adornmentIcon.notes}</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Estimated Finish Date"
                type="date"
                name="estimatedFinishDate"
                value={form.estimatedFinishDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">{adornmentIcon.estimatedFinishDate}</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 0.5 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/tickets")}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={submitting}
                  sx={{ fontWeight: 800 }}
                >
                  Create Ticket
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <AddCustomerModal
        open={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        onCreated={handleCustomerCreated}
      />


      {/* Seam to the fixed footer */}
      <Box sx={{ height: 12 }} />
    </Box>
  );
}

